import {
  lastLoggedSetFromEntry,
  lastLoggedSetsFromWorkouts,
  type LastLoggedAnySet,
  type Workout,
} from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { lastSessionEntryFromLogSets } from './lastSessionFromLogSets';
import { loadExerciseSets } from './useExerciseSetsFallback';

// The 50-session window cannot see an exercise resumed after a long break, so
// a start or a swap offered no weight for it while the history exists. Same
// order as the add-exercise prefill: the window first, the per-exercise index
// only for what the window does not hold.

/** Indexed reads in the air at once: a program session can ask for a dozen. */
export const FETCH_CONCURRENCY = 6;

export async function mapBounded<T, R>(
  items: readonly T[],
  limit: number,
  run: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      out[i] = await run(items[i] as T);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

/** An empty or failed index answer leaves the slug out: nothing to offer, not
 *  a verdict that the exercise has no history. */
export async function lastLoggedSetsBeyondWindow(
  slugs: ReadonlyArray<string>,
  window: ReadonlyArray<Workout>,
  apiFetch: ProgramApiFetch,
): Promise<Map<string, LastLoggedAnySet>> {
  const out = lastLoggedSetsFromWorkouts(window, slugs, { bodyweight: true });
  const missing = [...new Set(slugs)].filter((slug) => !out.has(slug));
  const found = await mapBounded(missing, FETCH_CONCURRENCY, async (slug) => {
    const entry = lastSessionEntryFromLogSets(await loadExerciseSets(slug, apiFetch));
    return entry ? lastLoggedSetFromEntry(entry, { bodyweight: true }) : undefined;
  });
  missing.forEach((slug, i) => {
    const last = found[i];
    if (last !== undefined) out.set(slug, last);
  });
  return out;
}
